import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, UserCheck, PauseCircle, PlayCircle, RefreshCcw, ClipboardList, Users, ScrollText } from "lucide-react";

const STATUS_COLOR = {
  approved: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-900",
  rejected: "bg-red-100 text-red-800",
  suspended: "bg-slate-200 text-slate-800",
};

export default function AdminApprovals() {
  const [pending, setPending] = useState([]);
  const [partners, setPartners] = useState([]);
  const [audit, setAudit] = useState([]);
  const [note, setNote] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/pending-onboardings"),
        api.get("/admin/partners"),
        api.get("/admin/audit-log?limit=100"),
      ]);
      setPending(a.data); setPartners(b.data); setAudit(c.data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (item, decision) => {
    try {
      await api.post(`/admin/pending-onboardings/${item.id}/decide`, {
        kind: item.kind, decision, note: note[item.id] || "",
      });
      toast.success(`${item.name || item.vehicle_number || "Item"} → ${decision}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const setPartnerStatus = async (u, status) => {
    try {
      await api.post(`/admin/partners/${u.id}/status`, { approval_status: status });
      toast.success(`${u.name} → ${status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const filteredPartners = partners.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.email.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-red-500/15 text-red-700 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Admin · Approvals</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Approve onboardings & partners</h1>
            <p className="text-slate-600 text-sm">Field-executive submissions are hidden from public listings until an admin approves them here.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="approvals-refresh"><RefreshCcw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending"><ClipboardList className="w-4 h-4 mr-2" /> Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="partners" data-testid="tab-partners"><Users className="w-4 h-4 mr-2" /> Partners ({partners.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-2" /> Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pending.length === 0 ? (
            <Card className="p-10 rounded-2xl text-center text-slate-500 mt-4" data-testid="no-pending">
              {loading ? "Loading…" : "Nothing pending — all onboardings are approved."}
            </Card>
          ) : (
            <div className="space-y-3 mt-4">
              {pending.map((it) => (
                <Card key={it.id} className="p-5 rounded-2xl" data-testid={`pending-${it.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] uppercase">{it.kind}</Badge>
                        <div className="font-display font-bold">{it.name || it.vehicle_number}</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{it.address || `${it.driver_name} · ${it.type?.toUpperCase()}`}</div>
                      {it.specialization && <div className="text-xs text-slate-500 mt-1">{it.specialization} · {it.experience || "?"}y</div>}
                      {it.tests && <div className="text-xs text-slate-500 mt-1">{it.tests.slice(0, 5).join(", ")}{it.tests.length > 5 ? `, +${it.tests.length - 5}` : ""}</div>}
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <Input placeholder="Approval note (optional)…" value={note[it.id] || ""} onChange={(e) => setNote({ ...note, [it.id]: e.target.value })} data-testid={`note-${it.id}`} />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => decide(it, "rejected")} data-testid={`reject-${it.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => decide(it, "approved")} data-testid={`approve-${it.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="partners">
          <div className="mt-4 flex items-center gap-2 mb-3">
            <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-search" />
          </div>
          <div className="space-y-3">
            {filteredPartners.map((p) => {
              const st = p.approval_status || "approved";
              return (
                <Card key={p.id} className="p-4 rounded-2xl flex items-center justify-between flex-wrap gap-3" data-testid={`partner-${p.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-teal-700" />
                      <div className="font-semibold">{p.name}</div>
                      <Badge className={`${STATUS_COLOR[st]} border-0 text-[10px] uppercase`}>{st}</Badge>
                      <Badge variant="outline" className="text-[10px] uppercase">{p.role}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{p.email} · {p.phone || "—"}{p.facility_kind ? ` · ${p.facility_kind}` : ""}</div>
                  </div>
                  <Select value={st} onValueChange={(v) => setPartnerStatus(p, v)}>
                    <SelectTrigger className="w-40" data-testid={`status-select-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="mt-4 p-5 rounded-2xl" data-testid="audit-log-panel">
            {audit.length === 0 ? (
              <div className="text-center text-slate-500 py-8">{loading ? "Loading…" : "No admin actions recorded yet."}</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {audit.map((a, i) => (
                  <div key={a.id || i} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`audit-row-${i}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] uppercase">{a.action}</Badge>
                        <span className="font-semibold text-sm">{a.target_kind}</span>
                        <span className="text-xs text-slate-400 font-mono">{a.target_id}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        by {a.actor_email || a.actor?.email || "admin"}
                        {a.meta?.note ? ` · "${a.meta.note}"` : ""}
                        {a.meta?.decision ? ` · decision: ${a.meta.decision}` : ""}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">
                      {a.at ? new Date(a.at).toLocaleString("en-IN") : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
